import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronDown } from 'lucide-react';
import { customerSkillsQueryOptions } from '../../lib/queries/customers';
import { useDebounce } from '../../hooks/useDebounce';

export default function Skills({ id }: { id: string }) {
  const [skillSearchInput, setSkillSearchInput] = useState('');
  const skillSearch = useDebounce(skillSearchInput, 600);
  const [skillStatus, setSkillStatus] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data: customerSkills = [], isLoading: skillsLoading } = useQuery(
    customerSkillsQueryOptions(id, skillSearch, skillStatus),
  );

  const badgeStyle: Record<string, string> = {
    published: 'border-success text-success',
    draft: 'border-accent text-accent',
    archived: 'border-subtle text-subtle',
  };

  const dotStyle: Record<string, string> = {
    published: 'bg-success',
    draft: 'bg-accent',
    archived: 'bg-subtle',
  };

  return (
    <div className="space-y-4">
      {/* Search + filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 md:max-w-xs">
          <Search className="text-primary/50 absolute top-2.5 left-2.5 h-4 w-4" />
          <input
            type="text"
            placeholder="Search skills"
            value={skillSearchInput}
            onChange={(e) => setSkillSearchInput(e.target.value)}
            className="bg-bg-elevated text-primary placeholder:text-primary/12 w-full rounded border border-white/8 py-2 pr-3 pl-8 text-sm focus:outline-none"
          />
        </div>

        {/* Mobile only: Filter dropdown */}
        <div className="relative md:hidden" ref={filterRef}>
          <button
            onClick={() => setFilterOpen((o) => !o)}
            className="bg-bg-elevated text-primary/70 hover:text-primary flex items-center gap-1.5 rounded border border-white/8 px-3 py-2 text-sm transition-colors"
          >
            Filter
            <ChevronDown className="h-4 w-4" />
          </button>
          {filterOpen && (
            <div className="bg-bg-elevated absolute top-full right-0 z-10 mt-1 w-36 rounded border border-white/8 shadow-lg">
              {(['', 'published', 'draft', 'archived'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setSkillStatus(s);
                    setFilterOpen(false);
                  }}
                  className={[
                    'w-full px-3 py-2 text-left text-sm transition-colors',
                    skillStatus === s
                      ? 'text-accent'
                      : 'text-primary/70 hover:text-primary',
                  ].join(' ')}
                >
                  {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tablet+ only: Pill status buttons */}
        {(['', 'published', 'draft', 'archived'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSkillStatus(s)}
            className={[
              'hidden rounded-full px-3 py-1 text-xs font-medium transition-colors duration-300 md:inline-flex',
              skillStatus === s
                ? 'bg-accent text-white'
                : 'bg-primary/8 text-primary/8 hover:bg-accent hover:text-primary',
            ].join(' ')}
          >
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {/* Skills list */}
      <div className="bg-bg-elevated divide-y divide-white/6 rounded-lg border border-white/6">
        {skillsLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              {/* Mobile skeleton dot */}
              <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-white/6 md:hidden" />
              {/* Tablet+ skeleton badge */}
              <div className="hidden h-5 w-14 animate-pulse rounded bg-white/6 md:block" />
              <div className="h-4 flex-1 animate-pulse rounded bg-white/6" />
            </div>
          ))
        ) : customerSkills.length === 0 ? (
          <p className="text-muted p-6 text-sm">No skills found.</p>
        ) : (
          customerSkills.map((skill) => {
            const updatedLabel = new Date(skill.updatedAt).toLocaleDateString(
              'en-US',
              { month: 'short', day: 'numeric' },
            );
            return (
              <div key={skill.id} className="flex items-center gap-4 px-4 py-3">
                {/* Mobile only: colored dot */}
                <span
                  className={[
                    'h-2.5 w-2.5 shrink-0 rounded-full md:hidden',
                    dotStyle[skill.status] ?? 'bg-white/20',
                  ].join(' ')}
                />
                {/* Tablet+ only: bordered status badge */}
                <span
                  className={[
                    'hidden shrink-0 rounded border px-2 py-0.5 text-xs font-medium capitalize md:inline-flex',
                    badgeStyle[skill.status] ?? 'text-muted border-white/20',
                  ].join(' ')}
                >
                  {skill.status === 'published' ? 'Active' : skill.status}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-primary truncate text-sm font-medium">
                    {skill.name}
                  </p>
                  {skill.description && (
                    <p className="text-subtle hidden truncate text-xs md:block">
                      {skill.description}
                    </p>
                  )}
                </div>
                <span className="text-subtle shrink-0 text-xs">
                  Updated {updatedLabel}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
